import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, Divider, Stack, Chip, Tabs, Tab,
  IconButton, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Tooltip, Paper, Avatar,
} from '@mui/material';
import {
  Close as CloseIcon, Phone as PhoneIcon, Email as EmailIcon,
  Business as BusinessIcon, Person as PersonIcon, Notes as NotesIcon,
  Edit as EditIcon, Delete as DeleteIcon, Send as SendIcon,
  AccountBalance as FundingsIcon, Assignment as SubmissionsIcon,
  Description as DocumentsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';
import { STATUS_CONFIG, TIMEZONE_LABELS, PRIORITY_COLORS } from './SalesDashboard';
import StatusChanger from './StatusChanger';
import QuickNoteBar from './QuickNoteBar';
import FundingsTab from './FundingsTab';
import SubmissionsTab from './SubmissionsTab';
import DocumentsTab from './DocumentsTab';

const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : null;
const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : null;

const NOTE_TYPE_ICONS = {
  call: '📞', email: '📧', sms: '💬', meeting: '🤝', note: '📝', status_change: '🔄',
};

const OUTCOME_LABELS = {
  no_answer: 'No Answer', left_voicemail: 'Voicemail Left',
  connected: 'Connected ✅', not_interested: 'Not Interested ❌',
  callback_requested: 'Callback Requested 📅', interested: 'Interested! 🎉',
  other: 'Other',
};

// ─────────────────────────────────────────────────────────────────────────────
export default function LeadDetailDrawer({ open, lead, onClose, onLeadUpdated, onLeadDeleted, onEditLead, onStatusChange }) {
  const [tab, setTab] = useState(0);
  const [fullLead, setFullLead] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);

  // Email composer state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch full lead (with all notes/emails) on open
  useEffect(() => {
    if (open && lead?._id) {
      setLoadingFull(true);
      setFullLead(null);
      salesAPI.getLead(lead._id)
        .then((res) => setFullLead(res.data.data))
        .catch(() => setFullLead(lead))
        .finally(() => setLoadingFull(false));
    }
  }, [open, lead?._id]);

  const displayLead = fullLead || lead;
  if (!displayLead) return null;

  const phone = displayLead.mobilePhone || displayLead.phone || displayLead.businessPhone;
  const statusCfg = STATUS_CONFIG[displayLead.status] || STATUS_CONFIG.new_lead;

  const handleLeadUpdated = (updated) => {
    setFullLead(updated);
    onLeadUpdated(updated);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await salesAPI.deleteNote(displayLead._id, noteId);
      handleLeadUpdated(res.data.data);
      toast.success('Note deleted');
    } catch { toast.error('Failed to delete note'); }
  };

  const handleDeleteLead = async () => {
    const name = displayLead.businessName || [displayLead.firstName, displayLead.lastName].filter(Boolean).join(' ') || 'this lead';
    if (!window.confirm(`Permanently delete "${name}"?\n\nThis cannot be undone.`)) return;
    try {
      await salesAPI.deleteLead(displayLead._id, { hard: true });
      toast.success('Lead deleted');
      onClose?.();
      onLeadDeleted?.(displayLead._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete lead');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { toast.warning('Subject and body required'); return; }
    setSendingEmail(true);
    try {
      const res = await salesAPI.sendEmail(displayLead._id, { subject: emailSubject.trim(), body: emailBody.trim() });
      handleLeadUpdated(res.data.data);
      setEmailSubject('');
      setEmailBody('');
      toast.success(res.data.emailStatus === 'sent' ? 'Email sent!' : 'Email logged (send may have failed)');
    } catch { toast.error('Failed to send email'); }
    finally { setSendingEmail(false); }
  };

  const handleSendReminder = async () => {
    if (!window.confirm(`Send an application reminder to ${displayLead.email}?`)) return;
    setSendingEmail(true);
    try {
      const res = await salesAPI.sendApplicationReminder(displayLead._id);
      handleLeadUpdated(res.data.data);
      toast.success(res.data.emailStatus === 'sent' ? 'Application reminder sent!' : 'Reminder logged (send may have failed)');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    }
    finally { setSendingEmail(false); }
  };

  // ── Email template quick-fill ─────────────────────────────────────────────
  const templates = [
    {
      label: 'Intro / Follow-up',
      subject: `Business Financing for ${displayLead.businessName || 'Your Business'}`,
      body: `Hi ${displayLead.firstName || 'there'},\n\nI wanted to reach out regarding potential financing options for ${displayLead.businessName || 'your business'}. We specialize in fast, flexible funding solutions including term loans, lines of credit, and merchant cash advances.\n\nBased on your business profile, you may qualify for up to ${currency(displayLead.requestedAmount) || 'significant funding'}. I'd love to connect and walk you through your options.\n\nWould you have 10 minutes this week for a quick call?\n\nBest regards,\nCore Business Capital\n(888) 000-0000\ncoreloanhub.com`,
    },
    {
      label: 'Application Follow-up',
      subject: `Update on Your Application – ${displayLead.businessName || ''}`,
      body: `Hi ${displayLead.firstName || 'there'},\n\nI'm following up on the financing application for ${displayLead.businessName || 'your business'}. We have reviewed your information and would like to discuss the next steps.\n\nPlease feel free to call me directly or reply to this email at your convenience.\n\nThank you,\nCore Business Capital`,
    },
    {
      label: 'Approval Notice',
      subject: `🎉 You've Been Approved! – ${displayLead.businessName || ''}`,
      body: `Hi ${displayLead.firstName || 'there'},\n\nGreat news — ${displayLead.businessName || 'your business'} has been approved for funding!\n\nPlease contact us as soon as possible to finalize the details and get your funds disbursed quickly.\n\nCongratulations!\nCore Business Capital`,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, bgcolor: statusCfg.color, color: '#fff' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>{displayLead.businessName || 'Lead Detail'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {[displayLead.firstName, displayLead.lastName].filter(Boolean).join(' ')}{displayLead.title ? ` · ${displayLead.title}` : ''}
            </Typography>
            <Stack direction="row" gap={0.75} mt={0.5} flexWrap="wrap">
              <Chip label={statusCfg.label} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }} />
              <Chip label={displayLead.priority} size="small" sx={{ bgcolor: PRIORITY_COLORS[displayLead.priority], color: '#fff', textTransform: 'capitalize' }} />
              <Chip label={TIMEZONE_LABELS[displayLead.timezone]?.split('(')[0]?.trim() || displayLead.timezone} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            </Stack>
          </Box>
          <Stack alignItems="flex-end">
            <IconButton onClick={onClose} sx={{ color: '#fff', mt: -0.5 }}><CloseIcon /></IconButton>
            <Stack direction="row">
              <Tooltip title="Edit Lead">
                <IconButton size="small" onClick={() => onEditLead(displayLead)} sx={{ color: '#fff' }}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Delete Lead">
                <IconButton size="small" onClick={handleDeleteLead} sx={{ color: '#fff' }}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>

        {/* Quick action buttons */}
        <Stack direction="row" gap={1} mt={1.5} mb={0.5}>
          {phone && (
            <Button variant="contained" startIcon={<PhoneIcon />} href={`tel:${phone}`} component="a" size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' }, fontWeight: 700 }}>
              {phone}
            </Button>
          )}
          {displayLead.email && (
            <Button variant="outlined" startIcon={<EmailIcon />} size="small" onClick={() => setTab(5)}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff', '&:hover': { borderColor: '#fff' } }}>
              Email
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Summary" icon={<BusinessIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 42, fontSize: '0.78rem' }} />
        <Tab icon={<SubmissionsIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 42, fontSize: '0.78rem' }}
          label={`Submissions (${displayLead.submissions?.length || 0})`}
        />
        <Tab label="Activity" icon={<NotesIcon fontSize="small" />} iconPosition="start"
          sx={{ minHeight: 42, fontSize: '0.78rem' }}
          {...(fullLead?.notes?.length ? { label: `Activity (${fullLead.notes.length})` } : {})}
        />
        <Tab icon={<FundingsIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 42, fontSize: '0.78rem' }}
          label={`Fundings (${displayLead.fundings?.length || 0})`}
        />
        <Tab label="Documents" icon={<DocumentsIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 42, fontSize: '0.78rem' }} />
        <Tab label="Send Email" icon={<EmailIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 42, fontSize: '0.78rem' }} />
      </Tabs>

      {/* ── Tab content ─────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {loadingFull && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}

        {/* ── SUMMARY TAB ──────────────────────────────────────────────────── */}
        {!loadingFull && tab === 0 && (
          <Box>
            {/* Status changer */}
            <Box mb={2}>
              <StatusChanger currentStatus={displayLead.status} onStatusChange={(s) => onStatusChange(displayLead._id, s)} />
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Business section */}
            <SectionHeader icon={<BusinessIcon />} title="Business Information" />
            <InfoGrid items={[
              { label: 'Business Name', value: displayLead.businessName },
              { label: 'Entity Type', value: displayLead.businessEntityType },
              { label: 'EIN', value: displayLead.businessEIN },
              { label: 'Industry', value: displayLead.industry },
              { label: 'Address', value: [displayLead.businessAddress, displayLead.businessCity, displayLead.businessState, displayLead.businessZip].filter(Boolean).join(', ') },
              { label: 'Business Phone', value: displayLead.businessPhone, href: displayLead.businessPhone ? `tel:${displayLead.businessPhone}` : null },
              { label: 'Website', value: displayLead.businessWebsite, href: displayLead.businessWebsite },
              { label: 'Monthly Revenue', value: currency(displayLead.monthlyRevenue) },
              { label: 'Requested Amount', value: currency(displayLead.requestedAmount) },
              { label: 'Funding Purpose', value: displayLead.fundingPurpose },
              { label: 'Outstanding Debt', value: displayLead.hasOutstandingDebt ? currency(displayLead.outstandingDebtBalance) || 'Yes' : 'None' },
              { label: 'Employees', value: displayLead.employees },
            ]} />

            {/* Owner 1 */}
            <SectionHeader icon={<PersonIcon />} title="Owner 1" />
            <InfoGrid items={[
              { label: 'Name', value: [displayLead.firstName, displayLead.lastName].filter(Boolean).join(' ') },
              { label: 'Title', value: displayLead.title },
              { label: 'Ownership %', value: displayLead.ownershipPct != null ? `${displayLead.ownershipPct}%` : null },
              { label: 'Mobile', value: displayLead.mobilePhone, href: displayLead.mobilePhone ? `tel:${displayLead.mobilePhone}` : null },
              { label: 'Phone', value: displayLead.phone, href: displayLead.phone ? `tel:${displayLead.phone}` : null },
              { label: 'Email', value: displayLead.email, href: displayLead.email ? `mailto:${displayLead.email}` : null },
              { label: 'Home Address', value: [displayLead.homeAddress, displayLead.homeCity, displayLead.homeState].filter(Boolean).join(', ') },
            ]} />

            {/* Owner 2 (if exists) */}
            {(displayLead.owner2FirstName || displayLead.owner2Email) && (
              <>
                <SectionHeader icon={<PersonIcon />} title="Owner 2" />
                <InfoGrid items={[
                  { label: 'Name', value: [displayLead.owner2FirstName, displayLead.owner2LastName].filter(Boolean).join(' ') },
                  { label: 'Title', value: displayLead.owner2Title },
                  { label: 'Ownership %', value: displayLead.owner2OwnershipPct != null ? `${displayLead.owner2OwnershipPct}%` : null },
                  { label: 'Phone', value: displayLead.owner2Phone, href: displayLead.owner2Phone ? `tel:${displayLead.owner2Phone}` : null },
                  { label: 'Email', value: displayLead.owner2Email, href: displayLead.owner2Email ? `mailto:${displayLead.owner2Email}` : null },
                ]} />
              </>
            )}

            {/* Funding outcome */}
            {displayLead.status === 'funded' && (displayLead.fundedAmount || displayLead.fundedDate) && (
              <>
                <SectionHeader icon={null} title="Funding Outcome" />
                <InfoGrid items={[
                  { label: 'Funded Amount', value: currency(displayLead.fundedAmount) },
                  { label: 'Funded Date', value: fmtDateTime(displayLead.fundedDate) },
                  { label: 'Program', value: displayLead.fundingProgram },
                ]} />
              </>
            )}

            {/* Assign + Meta */}
            <SectionHeader icon={null} title="Assignment & Meta" />
            <InfoGrid items={[
              { label: 'Assigned To', value: displayLead.assignedToName },
              { label: 'Source', value: displayLead.source?.replace(/_/g, ' ') },
              { label: 'Import Batch', value: displayLead.importBatch },
              { label: 'Added', value: fmtDateTime(displayLead.createdAt) },
              { label: 'Last Contact', value: fmtDateTime(displayLead.lastContactedAt) },
              { label: 'Total Call Attempts', value: displayLead.totalCallAttempts || 0 },
              { label: 'Follow-up Date', value: fmtDateTime(displayLead.nextFollowUpDate) },
            ]} />
          </Box>
        )}

        {/* ── SUBMISSIONS TAB ──────────────────────────────────────────────── */}
        {!loadingFull && tab === 1 && (
          <SubmissionsTab lead={displayLead} onLeadUpdated={handleLeadUpdated} />
        )}

        {/* ── ACTIVITY TAB ─────────────────────────────────────────────────── */}
        {!loadingFull && tab === 2 && (
          <Box>
            <QuickNoteBar leadId={displayLead._id} onLeadUpdated={handleLeadUpdated} />
            <Divider sx={{ my: 2 }} />

            {(!displayLead.notes || displayLead.notes.length === 0) && (
              <Typography color="text.secondary" textAlign="center" py={3}>No activity yet</Typography>
            )}

            <Stack gap={1.5}>
              {(displayLead.notes || []).map((note) => (
                <Paper key={note._id} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography fontSize="1rem">{NOTE_TYPE_ICONS[note.type] || '📝'}</Typography>
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {note.type} {note.outcome ? `· ${OUTCOME_LABELS[note.outcome] || note.outcome}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block">
                          {fmtDateTime(note.createdAt)} · {note.createdByName || 'System'}
                        </Typography>
                      </Box>
                    </Stack>
                    {note.type !== 'status_change' && (
                      <Tooltip title="Delete note">
                        <IconButton size="small" onClick={() => handleDeleteNote(note._id)}>
                          <DeleteIcon fontSize="small" sx={{ color: 'error.light' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>{note.content}</Typography>
                </Paper>
              ))}
            </Stack>

            {displayLead.emailsSent?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary">Emails Sent ({displayLead.emailsSent.length})</Typography>
                <Stack gap={1} mt={1}>
                  {displayLead.emailsSent.map((em) => (
                    <Paper key={em._id} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" fontWeight={700}>📧 {em.subject}</Typography>
                        <Chip label={em.status} size="small" color={em.status === 'sent' ? 'success' : 'error'} sx={{ height: 16, fontSize: '0.65rem' }} />
                      </Stack>
                      <Typography variant="caption" color="text.disabled">
                        {fmtDateTime(em.createdAt)} · {em.sentByName}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Box>
        )}

        {/* ── FUNDINGS TAB ─────────────────────────────────────────────────── */}
        {!loadingFull && tab === 3 && (
          <FundingsTab lead={displayLead} onLeadUpdated={handleLeadUpdated} />
        )}

        {/* ── DOCUMENTS TAB ────────────────────────────────────────────────── */}
        {!loadingFull && tab === 4 && (
          <DocumentsTab lead={displayLead} />
        )}

        {/* ── EMAIL TAB ────────────────────────────────────────────────────── */}
        {!loadingFull && tab === 5 && (
          <Box>
            {!displayLead.email && (
              <Typography color="error" textAlign="center" py={3}>No email address on file for this lead.</Typography>
            )}
            {displayLead.email && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Sending to: <strong>{displayLead.email}</strong>
                </Typography>

                {/* Template picker */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Quick Template</InputLabel>
                  <Select
                    label="Quick Template"
                    value=""
                    onChange={(e) => {
                      const tpl = templates.find((t) => t.label === e.target.value);
                      if (tpl) { setEmailSubject(tpl.subject); setEmailBody(tpl.body); }
                    }}
                  >
                    <MenuItem value="">— Choose a template —</MenuItem>
                    {templates.map((t) => <MenuItem key={t.label} value={t.label}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField
                  label="Subject"
                  fullWidth
                  size="small"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  label="Message"
                  fullWidth
                  multiline
                  minRows={10}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={sendingEmail ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                >
                  {sendingEmail ? 'Sending…' : 'Send Email'}
                </Button>

                <Divider sx={{ my: 2.5 }}>
                  <Typography variant="caption" color="text.secondary">Portal reminder</Typography>
                </Divider>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Sends the branded "finish your application" email. It lists this applicant's missing application items and documents, and requires a Core Loan Hub account with the same email.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleSendReminder}
                  disabled={sendingEmail}
                >
                  Send Application Reminder
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// ── Helper sub-components ─────────────────────────────────────────────────────
function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" gap={0.75} alignItems="center" mb={0.75} mt={2}>
      {icon && React.cloneElement(icon, { fontSize: 'small', sx: { color: 'text.secondary' } })}
      <Typography variant="overline" color="text.secondary" lineHeight={1}>{title}</Typography>
    </Stack>
  );
}

function InfoGrid({ items }) {
  const visible = items.filter((i) => i.value != null && i.value !== '' && i.value !== 'null');
  if (!visible.length) return <Typography variant="caption" color="text.disabled">No data</Typography>;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', mb: 1 }}>
      {visible.map(({ label, value, href }) => (
        <Box key={label}>
          <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.3}>{label}</Typography>
          {href
            ? <Typography variant="body2" component="a" href={href} sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500, wordBreak: 'break-all' }}>{value}</Typography>
            : <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>{value}</Typography>
          }
        </Box>
      ))}
    </Box>
  );
}
